import { expense } from '@/db/schema/expense'
import { user } from '@/db/schema/user'
import { ExtractedReceipt } from '@/lib/email-ingest/types'
import { relations } from 'drizzle-orm'
import { index, integer, jsonb, PgColumn, pgEnum, pgTable, serial, text, timestamp } from 'drizzle-orm/pg-core'

export const inboundReceiptStatus = pgEnum('inbound_receipt_status', ['pending', 'matched', 'rejected'])

/**
 * One row per receipt email forwarded to `expense@…`. Kept independently of the
 * expense it matches so a rejection (unknown sender, spam, no match) still
 * leaves an auditable record. `messageId` is unique so SNS redelivery of the
 * same email is a no-op.
 */
export const inboundReceipt = pgTable(
  'inbound_receipt',
  {
    id: serial('id').primaryKey(),
    created: timestamp().defaultNow().notNull(),
    updated: timestamp().defaultNow().notNull(),
    // RFC 5322 Message-ID — dedupes SNS redelivery of the same email.
    messageId: text('message_id').notNull().unique(),
    // Raw MIME location in the inbound SES bucket (eu-central-1).
    s3Key: text('s3_key').notNull(),
    fromEmail: text('from_email').notNull(),
    // null when the sender does not match an active member (row kept for audit).
    userId: integer('user_id').references((): PgColumn => user.id, { onDelete: 'set null' }),
    subject: text('subject'),
    extracted: jsonb('extracted').$type<ExtractedReceipt>(),
    // S3 keys of every stored PDF in the app bucket (eu-central-2).
    attachmentKeys: jsonb('attachment_keys').$type<string[]>(),
    status: inboundReceiptStatus('status').notNull(),
    rejectedReason: text('rejected_reason'),
    expenseId: integer('expense_id').references((): PgColumn => expense.id, { onDelete: 'set null' }),
  },
  (t) => ({
    userStatusIdx: index('inbound_receipt_user_status_idx').on(t.userId, t.status),
  })
)

export type InboundReceiptRow = typeof inboundReceipt.$inferSelect
export type InsertInboundReceipt = typeof inboundReceipt.$inferInsert

export const inboundReceiptRelations = relations(inboundReceipt, ({ one }) => ({
  user: one(user, { fields: [inboundReceipt.userId], references: [user.id] }),
  expense: one(expense, { fields: [inboundReceipt.expenseId], references: [expense.id] }),
}))
